
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Activity, BarChart3, Percent, Users } from "lucide-react";
import type { Client } from "@/types";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent
} from "@/components/ui/chart";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid
} from "recharts";

interface ClientMetricsSectionProps {
  client: Client;
}

export const ClientMetricsSection = ({ client }: ClientMetricsSectionProps) => {
  // Format data for the chart - last 7 days of activity
  const chartData = client.progress.completionRate.length > 0 
    ? client.progress.completionRate.map((value, index) => ({
        name: `Day ${index + 1}`,
        value
      })).slice(-7)
    : Array(7).fill(0).map((_, i) => ({ name: `Day ${i + 1}`, value: 0 }));

  const hasMetrics = client.totalSessions && client.totalSessions > 0;
  
  const config = {
    metric: {
      label: 'Performance',
      theme: { light: '#8B5CF6', dark: '#8B5CF6' },
    }
  };

  return (
    <Card className="backdrop-blur-lg bg-white/10 border-white/20 h-full overflow-hidden">
      <CardHeader>
        <CardTitle className="text-white flex items-center">
          <Activity className="h-5 w-5 mr-2 text-white/70" />
          Performance Metrics
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col">
        {hasMetrics ? (
          <>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="rounded-lg bg-white/5 p-3">
                <div className="flex items-center justify-between">
                  <p className="text-white/70 text-xs">Attendance</p>
                  <Percent className="h-4 w-4 text-white/50" />
                </div>
                <p className="text-white text-lg font-semibold">{client.progress.attendance || 0}%</p>
              </div>
              <div className="rounded-lg bg-white/5 p-3">
                <div className="flex items-center justify-between">
                  <p className="text-white/70 text-xs">Consistency</p>
                  <BarChart3 className="h-4 w-4 text-white/50" />
                </div>
                <p className="text-white text-lg font-semibold">{client.progress.consistency || 0}%</p>
              </div>
            </div>

            <div className="h-[120px] w-full">
              <ChartContainer className="h-full" config={config}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                  <XAxis dataKey="name" stroke="#999" fontSize={10} />
                  <YAxis stroke="#999" fontSize={10} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="value" fill="var(--color-metric)" />
                </BarChart>
              </ChartContainer>
            </div>
            
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div className="flex items-center justify-between rounded-lg bg-white/5 p-2">
                <p className="text-white/70 text-xs">Total Sessions</p>
                <p className="text-white text-sm font-medium">{client.totalSessions}</p>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-white/5 p-2">
                <p className="text-white/70 text-xs">Completed</p>
                <p className="text-white text-sm font-medium">{client.completedWorkouts}</p>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-[200px] text-center">
            <Users className="h-8 w-8 text-white/70 mb-2" />
            <p className="text-white/70 text-sm mb-2">No metrics available yet</p>
            <p className="text-white/50 text-xs">Data will appear after client completes workouts</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
